/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { PlayCircle as PlayCircleIcon, StopCircle as StopIcon } from 'components/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import PermissionButton from 'components/buttons/PermissionButton';
import TimecardHistoryButton from 'components/buttons/TimecardHistoryButton';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import HoursInput from 'components/forms/inputs/rhf-inputs/HoursInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { myCasesFilter, openCasesFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';
import { useGetMatterByIdQuery, useLazyGetMattersListQuery } from 'store/api/matters';
import { useAddTimecardMutation, useGetLastAddedTimecardPracticeAreasQuery } from 'store/api/timecards';
import { closeSubDrawer } from 'store/reducers/subdrawer';
import { RemunerationCalcType } from 'types/matters';
import { TimecardDto, TimecardMatterHistoryItem } from 'types/timecards';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { hoursToSeconds } from 'utils/time';
import { captureTimecardCreated } from 'utils/timecardAnalytics';
import { useClassifiers } from 'utils/useClassifiers';
import { DescriptionImprove } from '../form/DescriptionImprove';
import DescriptionLanguageCheck from '../form/DescriptionLanguageCheck';
import SearchMyCasesCheckbox from '../form/SearchMyCasesCheckbox';
import TimecardLanguageLabel from '../labels/TimecardLanguageLabel';
import { timecardFormToRequest } from '../timecardFormMapping';
import { useStartTrackingTimecard } from '../useStartTrackingTimecard';
import SectionTitle from './SectionTitle';

const TimecardCenterForm = (): JSX.Element => {
    const defaultValues: TimecardDto = {
        id: null,
        description: '',
        matter: null,
        employee: null,
        timecardDate: format(new Date(), DEFAULT_DATE_FORMAT),
        timecardDateUtcTimestamp: new Date().toISOString(),
        isNonBillable: false,
        multiplierRate: '1.0',
        multiplierTypeCode: 'NONE',
        practiceAreas: [],
        totalTimeInSeconds: null,
        totalTimeInHours: null,
        billedTimeInSeconds: 0,
        billedTimeInHours: '0',
        remunerationCalcType: RemunerationCalcType.EQUALS_BILLED_TIME,
        remunerationTimeInSeconds: 0,
        remunerationTimeInHours: '0',
        preserveNonBillableHours: false,
    };

    const navigate = useNavigate();
    const location = useLocation();
    const matterMatch = matchPath('/matter/:id/*', location.pathname);
    const urlMatterId = matterMatch?.params?.id;
    const { data: matterPrefilled } = useGetMatterByIdQuery(urlMatterId ?? skipToken);
    const { user } = useAuth();
    const userId = user?.id;
    const userName = user?.name;
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const [searchMyCases, setSearchMyCases] = useState(false);
    const { classifiers } = useClassifiers({
        codes: ['LANGUAGE'],
    });
    const dispatch = useDispatch();

    const [addTimecard] = useAddTimecardMutation();
    const { startTracking } = useStartTrackingTimecard();
    const { handleSubmit, control, setValue, setFocus, getValues, watch, subscribe } = useForm<TimecardDto>({
        defaultValues,
    });
    const { dirtyFields } = useFormState({ control });

    // RHF `setValue` is a framework API for syncing form state with async props (auth/url params).
    // Not a no-effect-event-handler violation — there's no originating event to attach to.
    useEffect(() => {
        if (userId && userName) {
            setValue('employee', { id: userId, name: userName });
        }
    }, [setValue, userId, userName]);

    // TODO: effect-event-handler skip — RHF `setValue` framework API syncing prop into form state.
    useEffect(() => {
        if (matterPrefilled) {
            setValue('matter', matterPrefilled);
        }
    }, [matterPrefilled, setValue]);

    const onTotalTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const hoursValue = event.target.value;

            let secondsValue: number | null = null;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }

            setValue('totalTimeInSeconds', secondsValue);
        },
        [setValue]
    );

    const currentMatter = watch('matter');
    const { data: lastAddedTimecardPracticeAreas } = useGetLastAddedTimecardPracticeAreasQuery(
        {
            employeeId: userId?.toString() || '',
            matterId: currentMatter?.id || 0,
        },
        { skip: !currentMatter }
    );

    useEffect(() => {
        if (lastAddedTimecardPracticeAreas?.length) {
            setValue('practiceAreas', lastAddedTimecardPracticeAreas);
        }
    }, [lastAddedTimecardPracticeAreas, setValue]);

    const mattersFilter = searchMyCases ? `${myCasesFilter};${openCasesFilter}` : openCasesFilter;

    const onFinish = (data: TimecardDto) => {
        dispatch(closeSubDrawer());
        navigate(`/timecards/finish/`, {
            state: {
                timecardData: {
                    ...data,
                    description: data?.description ?? '',
                    totalTimeInSeconds: data?.totalTimeInSeconds ?? 0,
                },
            },
        });
    };

    const onCreateDraft = async (data: TimecardDto) => {
        try {
            await addTimecard(
                timecardFormToRequest({
                    ...data,
                    description: data?.description ?? '',
                    totalTimeInSeconds: data?.totalTimeInSeconds ?? 0,
                })
            ).unwrap();
            void captureTimecardCreated({
                via: 'tracker',
                kind: 'draft',
                description: data.description,
                timecardLanguageCode: data.matter?.timecardLanguageCode,
            });
            dispatch(closeSubDrawer());
            navigate(`/timecards`);
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const onStartTracking = async (data: TimecardDto) => {
        try {
            const timecard = await addTimecard(
                timecardFormToRequest({
                    ...data,
                    description: data?.description ?? '',
                    totalTimeInSeconds: data?.totalTimeInSeconds ?? 0,
                })
            ).unwrap();
            void captureTimecardCreated({
                via: 'tracker',
                kind: 'draft',
                description: data.description,
                timecardLanguageCode: data.matter?.timecardLanguageCode,
            });

            // Awaited so a failed start lands in the catch below instead of an unhandled rejection.
            await startTracking(timecard.id);
            dispatch(closeSubDrawer());
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const onSubmit = async (data: TimecardDto, action: 'createDraft' | 'finish' | 'start'): Promise<void> => {
        if (action === 'createDraft') {
            onCreateDraft(data);
        } else if (action === 'finish') {
            onFinish(data);
        } else if (action === 'start') {
            onStartTracking(data);
        }
    };

    const handleTimecardHistoryCopy = useCallback(
        (timecard: TimecardMatterHistoryItem) => {
            if (!dirtyFields.description || getValues('description').length === 0) {
                setValue('description', timecard.description || '', { shouldDirty: false });
            }
        },
        [dirtyFields, getValues, setValue]
    );

    return (
        <>
            <SectionTitle
                title="timecards.create-new-timecard"
                rightComponent={<SearchMyCasesCheckbox value={searchMyCases} onChange={setSearchMyCases} />}
            />
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                required
                name="matter"
                paginated={{
                    fetch: useLazyGetMattersListQuery,
                    searchField: ['fullName', 'clientParty.name'],
                    filters: mattersFilter,
                    sort: ['clientParty.name,asc', 'name,asc'],
                }}
                groupByField="clientParty.name"
                getOptionLabel="fullName"
                getOptionIdentifier="id"
                placeholder="timecards.form.select-case"
                placeholderOnFocus="timecards.form.search-case-by"
                getSelectedValueStartAdornment={renderMatterIndicators}
                customOptionRenderer={(option, props, getValue) =>
                    renderMatterOptionWithRestrictedSelection(
                        option,
                        props,
                        getValue,
                        true,
                        'dashboard.tooltips.timecard-blocked'
                    )
                }
            />
            <DescriptionLanguageCheck
                getValues={getValues}
                setValue={setValue}
                subscribe={subscribe}
                timecardLanguageCode={currentMatter?.timecardLanguageCode}
                via="tracker"
            >
                {(isLanguageMismatch, checkDescriptionLanguage, aiEdit) => (
                    <DescriptionImprove
                        control={control}
                        getValues={getValues}
                        setFocus={setFocus}
                        aiEdit={aiEdit}
                        isMismatch={isLanguageMismatch}
                        checkDescription={checkDescriptionLanguage}
                        matterId={currentMatter?.id}
                        timecardLanguageCode={currentMatter?.timecardLanguageCode}
                        via="tracker"
                    >
                        {(improveRow, improveBox, rulesButton) => (
                            // The one-line field has no visible label: History, the rules and the language chip sit
                            // inside it, so it, the row and the box take the drawer's whole width.
                            <div className="mt-2 mb-1 flex flex-col">
                                <TextInput
                                    name="description"
                                    control={control}
                                    onBlur={checkDescriptionLanguage}
                                    placeholder="timecards.form.description"
                                    suffixComponent={
                                        <>
                                            <TimecardHistoryButton
                                                matterId={currentMatter?.id}
                                                handleTimecardCopy={handleTimecardHistoryCopy}
                                                inField
                                            />
                                            {rulesButton}
                                            {currentMatter?.timecardLanguageCode && (
                                                <TimecardLanguageLabel
                                                    classifierData={classifiers?.['LANGUAGE']}
                                                    timecardLanguageTypeCode={currentMatter.timecardLanguageCode}
                                                    type={isLanguageMismatch ? 'warning' : 'neutral'}
                                                />
                                            )}
                                        </>
                                    }
                                />
                                {improveRow}
                                {improveBox && <div className="mb-2">{improveBox}</div>}
                            </div>
                        )}
                    </DescriptionImprove>
                )}
            </DescriptionLanguageCheck>
            <div className="new:max-sm:flex-wrap flex flex-row gap-2">
                <DateInput
                    fullWidth
                    // On a phone the field has the panel's whole row, and the date in the 16px face still does not
                    // fit beside the four buttons at their own spacing: they close up to leave it room.
                    wrapperClassName="new:max-sm:[&_[data-slot=input-group-addon]]:gap-1"
                    maxDate={new Date()}
                    control={control}
                    name="timecardDate"
                    required
                    useIncrementDecrementButtons
                    setValue={setValue}
                />
                <HoursInput
                    control={control}
                    name="totalTimeInHours"
                    placeholder="timecards.form.total-time"
                    suffixComponent="h"
                    onChange={onTotalTimeChange}
                    showMinutesHint
                />
            </div>
            <div className="new:flex-wrap flex flex-row gap-2 pt-2 pb-4">
                <PermissionButton
                    permissions={['CREATE.TIMECARDS']}
                    variant="contained"
                    color="secondary"
                    startIcon={<PlayCircleIcon className="size-6" />}
                    onClick={(e) => {
                        void handleSubmit((data) => onSubmit(data, 'start'))(e);
                    }}
                    disabled={buttonsDisabled}
                >
                    {t('timecards.start')}
                </PermissionButton>
                <PermissionButton
                    permissions={['CREATE.TIMECARDS']}
                    variant="outlined"
                    color="secondary"
                    startIcon={<StopIcon className="size-6" />}
                    onClick={(e) => {
                        void handleSubmit((data) => onSubmit(data, 'finish'))(e);
                    }}
                >
                    {t('timecards.finish')}
                </PermissionButton>
                <PermissionButton
                    permissions={['CREATE.TIMECARDS']}
                    variant="outlined"
                    color="secondary"
                    onClick={(e) => {
                        void handleSubmit((data) => onSubmit(data, 'createDraft'))(e);
                    }}
                    disabled={buttonsDisabled}
                >
                    {t('timecards.save-as-draft')}
                </PermissionButton>
            </div>
        </>
    );
};

export default TimecardCenterForm;
